import { useState, useRef, useEffect, useMemo } from "react";
//import { useGetTitle } from "../../../hooks/useGetTitle";
import { useParams } from "react-router-dom";
//import { toast } from "react-toastify";

import { 
  Box, Breadcrumbs, Button, 
  Chip,
  Collapse, Checkbox,
  Divider,
  FormControl, FormControlLabel,
  IconButton, InputAdornment,
  OutlinedInput,
  Paper,
  Stack,
  Switch,
  Typography, TextField, 
  useMediaQuery,
  useTheme 
} from "@mui/material";

//import styles from "../../main/main.module.css";
import styled from "styled-components";

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { 
  useGetTableData, useGetTableDataInQueryString, 
  useCreateTableData,
  usePutTableData,
  useDeleteTableData,
} from "../../../components/Table/hooks";

import { standardProcessColumns, standardProcessColumns_admin } from "../../../constants/columns";
import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import { 
  BreadcrumbsTable,  
  ConfirmDialog, 
  deepCopy,
  SnackbarAlert, 
  formatDateWithDayjs, formatDateToDayjs,
} from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1199px) {
    padding: 12px;
    overflow: auto;
  }
`;

///////////////////////////////
// 정보관리의 공정등록 컴포넌트
///////////////////////////////
const PARENT_TITLE = "정보관리";
const CHILD_TITLE = "대상 공정 등록";
export function StandardProcess() {
  const theme = useTheme();
  const isReadOnly = false;
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("lg"));
  const { group_code, project_code } = useParams();

  const UPDATE_STATUS = Object.freeze({ // 수정 상태
    EDIT: 'edit',
    ROW_ORDERING: 'rowOrdering',
    DELETE: 'delete',
  });

  const FILTER_STATUS = Object.freeze({ // 필터 상태
    ALL: "all",
    REGISTERED: "registered",
  });

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 조건을 상태로 관리합니다.
  const [searchStatus, setSearchStatus] = useState({ filter: FILTER_STATUS.REGISTERED });
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = { 
    params: { // 쿼리스트링 만들때 params 이름을 사용하는게 규칙입니다.
      filter: searchStatus.filter,
    },
  }

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getSafeProcess", 
    group_code,
    project_code,
    qeuryString,
  );
  // 데이터 생성 요청
  const postData = useCreateTableData( 
    "request",
    "/postSafeProcess",
    group_code,
    project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData( 
    "request",
    "/updateSafeProcess",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteSafeProcess",
    group_code,
    project_code
  );

  // 조회된 데이터 임시 저장 상태 관리
  const [tempData, setTempData] = useState([]);
  useEffect(() => {
    // 데이터 가공
    const data = getData.data.map(item =>{
      return {
        ...item,
        __bgColor: item.process_status === 1 
          ? {
            backgroundColor: "#ffebeb", // 연한 빨간색 배경
            backgroundImage:
              "repeating-linear-gradient(45deg, #ffcccc 0, #ffcccc 2px, transparent 2px, transparent 6px)",
            backgroundSize: "8px 8px",
          }
          : theme.palette.background.paper,
      }
    });
    setTempData(data);
  },[getData.data]);

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const [compactSelectedRowIds, setCompactSelectedRowIds] = useState({});
  const getSelectedRows = () => {
    if (isCompactLayout) {
      return tempData
        .filter((row) => compactSelectedRowIds[String(row.process_id)])
        .map((row) => ({ original: row }));
    }
    return tableRef.current?.getSelectedRows() || [];
  };
  const clearAllSelection = () => {
    clearSelection();
    setCompactSelectedRowIds({});
  };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const [showAdminMode, setShowAdminMode] = useState(false); // 관리자 모드 활성화 여부
  const compactSelectedCount = Object.keys(compactSelectedRowIds).length;
  const filteredCompactData = useMemo(() => {
    const keyword = globalFilter.trim().toLowerCase();
    if (!keyword) return tempData;

    return tempData.filter((row) => {
      const values = [
        row.process_index,
        row.process_name,
        row.process_description,
        row.create_date ? dayjs(row.create_date).format("YYYY-MM-DD") : "",
        row.delete_date ? dayjs(row.delete_date).format("YYYY-MM-DD") : "",
      ];
      return values.some((value) => String(value ?? "").toLowerCase().includes(keyword));
    });
  }, [globalFilter, tempData]);
  const toggleCompactRowSelection = (processId) => {
    const key = String(processId);
    setCompactSelectedRowIds((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };
  const customTableTopToolbar = (table) => {
    return(
      <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
        {/* 왼쪽 영역 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* TODO: */}
        </Box>
        
        {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* 검색창 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setShowSearch((prev) => !prev)}>
              <SearchIcon />
            </IconButton>
        
            <Collapse in={showSearch} orientation="horizontal">
              <TextField
                size="small"
                placeholder="검색"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  table.setGlobalFilter(e.target.value);
                }}
                sx={{ minWidth: 200 }}
                variant="outlined"
                slots={{ input: OutlinedInput}}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setGlobalFilter('');
                            table.setGlobalFilter('');
                            setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                    ),
                  }
                }}
              />
            </Collapse>
          </Box>
          {!isReadOnly ? (
            <>
              {/* 추가 버튼 */}
              <Buttons.Add
                onClick={() => openAddModal()} // 행 생성 다이얼로그 열기
                sx={{ padding: '12px 16px' }}
              />
              {/* 삭제 버튼 */}
              {!showAdminMode && 
                <Buttons.Delete 
                  onClick={openDeleteModal} // 행 삭제 로직 실행
                  disabled={table.getSelectedRowModel().rows.length === 0}
                  sx={{ padding: '12px 16px' }}
                />    
              }

              {/* 데이터 삭제 버튼 */}
              {showAdminMode && 
                <Buttons.Delete 
                  onClick={openDeleteRecordModal} // 행 삭제 로직 실행
                  disabled={table.getSelectedRowModel().rows.length === 0}
                  sx={{ padding: '12px 16px',}}
                >
                  데이터 삭제
                </Buttons.Delete>
              }
              {/* 관리자 모드 */}
              <FormControl component="fieldset">
                <FormControlLabel
                  value="top"
                  control={
                    <Switch color="primary" 
                      checked={showAdminMode}
                      onChange={(event) => {
                        setShowAdminMode(event.target.checked);
                        setSearchStatus({ ...searchStatus, filter: event.target.checked ? FILTER_STATUS.ALL : FILTER_STATUS.REGISTERED});
                        clearAllSelection();
                      }}
                    />
                  }
                  label="관리자모드"
                  labelPlacement="end" // bottom,end,start,top
                  sx={{marginLeft : '4px'}}
                />
              </FormControl>    
            </>
          ) : null}
        </Box>
      </Box>
    );
  };

  // 공정등록 추가 모달 상태 관리
  const [addModal, setAddModal] = useState({ isOpen: false });
  const openAddModal = () => setAddModal({ isOpen: true });
  const closeAddModal = () => setAddModal({ isOpen: false });
  // DB 업데이트(추가 데이터)
  const updateAddData = async (addData) => {
    try {
      //console.log("새로운 데이터: ", addData);

      // 데이터베이스 업데이트
      const result = await postData.mutateAsync(addData);
      if(result.status === 200) {
        openDataAlert(message.addSuccess);
      }

      // 생성 모달 초기화
      closeAddModal();
    }
    catch (error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  // 공정등록 수정 모달 상태 관리
  const [editModal, setEditModal] = useState({ isOpen: false, rowData: [] });
  const openEditModal = (rowData) => setEditModal({ isOpen: true, rowData: rowData});
  const closeEditModal = () => setEditModal({ isOpen: false, rowData: [] });
  // 공정등록 테이블의 행 수정 액션
  const customRenderRowActionContent = (rowData) => {
    return(
      <Buttons.Edit
        onClick={(event) =>{
          event.stopPropagation(); // 이벤트 전파 중단
          openEditModal(rowData);
        }}
        sx={{ padding: '8px 12px' }}
      />
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    //console.log(updateData);
    try {
      const data ={
        updateData: updateData,
        updateStatus: UPDATE_STATUS.EDIT,
      };

      const result = await putData.mutateAsync(data);
      if(result.status === 200) {
        openDataAlert(message.updateEditSuccess);
      }

      // 수정 모달 초기화
      closeEditModal();
    }
    catch (error) {
        console.error("데이터 수정 중 오류 발생:", error);
    }
  }
  // DB 업데이트 (행 순서 변경)
  const updateRowOrderingData = async (draggedRowData, hoveredRowData) =>{
    try {
      //console.log('드래그한 row의 데이터:', draggedRowData);
      //console.log('호버된 row의 데이터:', hoveredRowData);

      const data = {
        updateData: {
          dragged_process_id: draggedRowData.process_id,
          dragged_process_index: draggedRowData.process_index,
          hovered_process_id: hoveredRowData.process_id,
          hovered_process_index: hoveredRowData.process_index,
        },
        updateStatus: UPDATE_STATUS.ROW_ORDERING,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);
      if(result.status) {
        openDataAlert(message.updateEditSuccess);
      }
      clearAllSelection();
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  };

  // 공정등록 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });

  // DB 업데이트 (삭제 데이터)
  const updateDeleteData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    // 여기에 실제 동작 실행 코드
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);

      const data = {
        updateData: selectedRows,
        updateStatus: UPDATE_STATUS.DELETE,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearAllSelection();
      
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  // 레코드 삭제 모달 상태 관리
  const [deleteRecordModal, setDeleteRecordModal] = useState({ isOpen: false });
  const openDeleteRecordModal = () => setDeleteRecordModal({ isOpen: true });
  const closeDeleteRecordModal = () => setDeleteRecordModal({ isOpen: false });

  // DB 업데이트 (레코드 데이터 삭제) 
  const deleteRecordData = async () => {
    closeDeleteRecordModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteData: selectedRows
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearAllSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    if (isCompactLayout) {
      return (
        <TableContainer>
          <Stack spacing={1.5} sx={{ minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="공정명, 공정 내용 검색"
                  value={globalFilter}
                  onChange={(event) => setGlobalFilter(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: globalFilter ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setGlobalFilter("")}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  justifyContent={{ xs: "space-between", sm: "flex-end" }}
                  sx={{ flexWrap: "wrap", rowGap: 0.75 }}
                >
                  <Buttons.Add onClick={() => openAddModal()} sx={{ minHeight: 38, px: 1.5 }} />
                  {!showAdminMode ? (
                    <Buttons.Delete
                      onClick={openDeleteModal}
                      disabled={compactSelectedCount === 0}
                      sx={{ minHeight: 38, px: 1.5 }}
                    />
                  ) : (
                    <Buttons.Delete
                      onClick={openDeleteRecordModal}
                      disabled={compactSelectedCount === 0}
                      sx={{ minHeight: 38, px: 1.5 }}
                    >
                      데이터 삭제
                    </Buttons.Delete>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        size="small"
                        checked={showAdminMode}
                        onChange={(event) => {
                          setShowAdminMode(event.target.checked);
                          setSearchStatus({
                            ...searchStatus,
                            filter: event.target.checked ? FILTER_STATUS.ALL : FILTER_STATUS.REGISTERED,
                          });
                          clearAllSelection();
                        }}
                      />
                    }
                    label="관리자모드"
                    sx={{
                      m: 0,
                      "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 700 },
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Stack spacing={1}>
              {filteredCompactData.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    textAlign: "center",
                    color: "text.secondary",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography fontWeight={800}>표시할 공정이 없습니다.</Typography>
                </Paper>
              ) : (
                filteredCompactData.map((row) => {
                  const rowKey = String(row.process_id);
                  const selected = Boolean(compactSelectedRowIds[rowKey]);
                  const isDeleted = Number(row.process_status) === 1;
                  return (
                    <Paper
                      key={rowKey}
                      variant="outlined"
                      sx={{
                        p: { xs: 1.35, sm: 1.5 },
                        borderRadius: 2,
                        borderColor: selected ? "primary.main" : "divider",
                        bgcolor: isDeleted ? "rgba(255, 235, 235, 0.72)" : "background.paper",
                        boxShadow: selected ? "0 8px 20px rgba(99, 102, 241, 0.12)" : "none",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Checkbox
                          size="small"
                          checked={selected}
                          onChange={() => toggleCompactRowSelection(row.process_id)}
                          sx={{ p: 0.25, mt: 0.15 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 0.5, minWidth: 0 }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                              <Chip
                                size="small"
                                label={`No. ${row.process_index ?? "-"}`}
                                sx={{ height: 24, fontWeight: 800, borderRadius: 1 }}
                              />
                              {isDeleted ? (
                                <Chip
                                  size="small"
                                  label="삭제됨"
                                  color="error"
                                  variant="outlined"
                                  sx={{ height: 24, fontWeight: 800, borderRadius: 1 }}
                                />
                              ) : null}
                            </Stack>
                            <Buttons.Edit
                              onClick={() => openEditModal(row)}
                              sx={{ minWidth: 0, px: 1.25, py: 0.65 }}
                            />
                          </Stack>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: "text.primary",
                              lineHeight: 1.35,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.process_name || "-"}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.65,
                              color: "text.secondary",
                              fontSize: 13,
                              lineHeight: 1.45,
                              display: "-webkit-box",
                              WebkitLineClamp: { xs: 3, sm: 2 },
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              wordBreak: "keep-all",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {row.process_description || "-"}
                          </Typography>
                          {showAdminMode ? (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.75 }}
                            >
                              <Chip
                                size="small"
                                label={`등록 ${row.create_date ? dayjs(row.create_date).format("YYYY-MM-DD") : "-"}`}
                                variant="outlined"
                                sx={{ borderRadius: 1, fontSize: 12 }}
                              />
                              <Chip
                                size="small"
                                label={`삭제 ${row.delete_date ? dayjs(row.delete_date).format("YYYY-MM-DD") : "-"}`}
                                variant="outlined"
                                sx={{ borderRadius: 1, fontSize: 12 }}
                              />
                            </Stack>
                          ) : null}
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })
              )}
            </Stack>
          </Stack>

          {renderDialogs()}
        </TableContainer>
      );
    }

    return (
      <TableContainer>
        {/** 공정등록 테이블 표시 */}
        <NewTableTemplate
          ref={tableRef}
          columns={showAdminMode ? standardProcessColumns_admin : standardProcessColumns}
          data={tempData}
          topToolbarContent={customTableTopToolbar}
          renderRowActionContent={isReadOnly ? null : customRenderRowActionContent}
          onRowOrdering={updateRowOrderingData}
          enableCustomGlobalFilter={true}
          enableRowOrdering={!showAdminMode && !isReadOnly} // 관리자 모드에서는 행 순서 변경 비활성화
          enabledSelectionAllRow={!isReadOnly}
          rowActionsColumnSize={96}
          //enableRowOrdering={true}
          //enabledSelectionAllRow={false}
          templateSX ={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
        {/* 행(row) 추가 모달 */}
        <CreateRowModal 
          isOpen={addModal.isOpen}
          originalRowData={deepCopy(tempData)}
          onConfirm={updateAddData}
          onCancel={closeAddModal}
        />
        {/* 행(row) 수정 모달 */}
        {showAdminMode ?
          <EditRowModalAdmin 
            isOpen={editModal.isOpen}
            originalRowData={editModal.rowData}
            onConfirm={updateEditData}
            onCancel={closeEditModal}
          /> :
          <EditRowModal
            isOpen={editModal.isOpen}
            originalRowData={editModal.rowData}
            onConfirm={updateEditData}
            onCancel={closeEditModal}
          />
        }
        {renderDialogs()}
      </TableContainer>
    );
  }

  function renderDialogs() {
    return (
      <>
        {/* 삭제 메시지 */}
        <ConfirmDialog
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={updateDeleteData}
          onCancel={closeDeleteModal}
        />
        {/** 레코드 삭제 메시지 */}
        <ConfirmDialog
          open={deleteRecordModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteRecordData}
          onCancel={closeDeleteRecordModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
      </>
    );
  }

  // StandardProcess 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

// 테이블의 행(row) 추가 모달
const CreateRowModal = ({ isOpen, originalRowData, onConfirm, onCancel }) => {
  const theme = useTheme();
  // const { group_code, project_code } = useParams();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 추가 행 데이터 관리
  const [addData, setAddData] = useState([]);
  const clearAddData = () => setAddData([]);

  // 행 추가 완료 액션
  const addAction = () => {
    if(checkEmptyData(addData)) {
      openValidationAlert(message.notAllowedEmpty);
      //toast.error(message.notAllowedEmpty);
      return;
    } 

    // TODO: 추후 백엔드에서 인덱스 처리 시 주석 처리
    //console.log("새로운 데이터: ", addData);
    const index = originalRowData.length > 0 ? [...originalRowData].pop().process_index + 1 : 1;
    const data = {
      ...addData,
      process_index: index, // 마지막 인덱스
    };

    onConfirm(data);
    clearAddData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.process_name || !item.process_description
    );
    return hasValidData;
  };

  // CreateRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={addAction}
      handleCancel={() => {
        clearAddData();
        onCancel();
      }}
      title="추가하기"
      cancelText="취소"
      confirmText="추가"
      containerSX={{
        width: { xs: 'calc(100vw - 24px)', sm: 460, lg: 400 },
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: '90vh',
      }}
    >
      <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 3, py: 2}}>
        {/* 공정명 */}
        <TextField
          label={'공정명'}
          value={addData?.process_name || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              process_name: e.target.value,
            });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        />
        {/* 공정내용 */}
        <TextField
          label={'공정내용'}
          value={addData?.process_description || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              process_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
}

// 테이블의 행(row) 수정 모달
const EditRowModal = ({ isOpen, originalRowData, onConfirm, onCancel }) => {

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() =>{
    setEditData(originalRowData);
  },[originalRowData])

  // 행 수정 완료 액션
  const editAction = () => {
    if(checkEmptyData(editData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    } 
    //console.log("수정된 데이터:", editData);
      
    onConfirm(editData);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.process_name || !item.process_description
    );
    return hasValidData;
  };

  // EditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={editAction}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      containerSX={{
        width: { xs: 'calc(100vw - 24px)', sm: 460, lg: 400 },
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: '90vh',
      }}
      contentSX={{ gap: "24px" }}
    >
      <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 3, py: 2}}>
        <TextField
          label={'공정명'}
          value={editData?.process_name || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              process_name: e.target.value,
            });
          }}
        />
        <TextField
          label={'공정내용'}
          value={editData?.process_description || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              process_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
}
// 테이블의 행(row) 수정 모달 - 관리자 모드
const EditRowModalAdmin = ({ isOpen, originalRowData, onConfirm, onCancel }) => {

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() =>{
    if(!originalRowData) {
      return;
    }

    const tempData = { ...originalRowData };
    tempData['create_date'] = formatDateToDayjs(tempData['create_date']);
    tempData['delete_date'] = formatDateToDayjs(tempData['delete_date']);
    setEditData(tempData);
  },[originalRowData])

  // 행 수정 완료 액션
  const editAction = () => {
    if(checkEmptyData(editData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    } 
    if(checkCreateDate(editData)) {
      openValidationAlert(message.notAllowedEmptyRegisterDate);
      return;
    }
    //console.log("수정된 데이터:", editData);

    // 데이터 포맷 변경
    const data = { 
      ...editData, 
      create_date: formatDateWithDayjs(editData.create_date),
      delete_date: formatDateWithDayjs(editData.delete_date),
      process_status: editData.delete_date ? 1 : 0, // 삭제일자가 있으면 1(삭제됨), 없으면 0(등록됨)
    };

    onConfirm(data);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.process_name || !item.process_description
    );
    return hasValidData;
  };
  // 등록일자 체크
  const checkCreateDate = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제
    
    const hasValidData = tempData.some(
      item => !item.create_date
    );
    return hasValidData;
  };

  // EditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={editAction}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      containerSX={{
        width: { xs: 'calc(100vw - 24px)', sm: 480, lg: 400 },
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: '90vh',
      }}
      contentSX={{ gap: "24px" }}
    >
      <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 3, py: 2}}>
        <TextField
          label={'공정명'}
          value={editData?.process_name || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              process_name: e.target.value,
            });
          }}
        />
        <TextField
          label={'공정내용'}
          value={editData?.process_description || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              process_description: e.target.value,
            });
          }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <DatePicker
            label={'등록일자'}
            value={editData?.create_date || null}
            onChange={(e) => {
              setEditData({
                ...editData,
                create_date: e,
              });
            }}
            slotProps={{
              field: { clearable: true },
              // textField: {
              //   fullWidth: true,
              //   sx: {
              //     marginTop: '16px',
              //     marginBottom: '8px',
              //   },
              // },
            }}
          />
          <DatePicker
            label={'삭제일자'}
            value={editData?.delete_date || null}
            onChange={(e) => {
              setEditData({
                ...editData,
                delete_date: e,
              });
            }}
            slotProps={{
              field: { clearable: true },
              // textField: {
              //   fullWidth: true,
              //   sx: {
              //     marginTop: '16px',
              //     marginBottom: '8px',
              //   },
              // },
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
}
